import { apiFetch } from '@/utils/api.js'
import useAuthStore, { isUserToken } from '@/stores/authStore'
import type { Resource } from '@/types/_general'
import type { components } from '@/types/_api'

type ActivitySchema = components['schemas']['Application-UserActivity-Activity']

/** Possible values for the domain_action field */
export type ResourceAction = NonNullable<ActivitySchema['domain_action']>

/** Possible values for the portal field */
export type PortalAction = ActivitySchema['portal']

/** Payload for language switch activity */
export interface LanguageSwitchPayload {
  from: string
  to: string
}

/** Base activity payload with required fields */
interface BaseActivity {
  name: string
  timestamp: Date
}

/** Activity with portal field */
interface PortalActivity extends BaseActivity {
  portal: PortalAction
}

/** Activity with route field */
interface RouteActivity extends BaseActivity {
  route: string
}

/** Activity with domain field (and optional domain_action) */
interface DomainActivity extends BaseActivity {
  domain: Resource
  domain_action?: ResourceAction
}

/** Activity with language_switch field */
interface LanguageSwitchActivity extends BaseActivity {
  language_switch: LanguageSwitchPayload
}

/** Union of all activity types */
type UserActivity = PortalActivity | RouteActivity | DomainActivity | LanguageSwitchActivity

/**
 * Check if activity tracking is available.
 * Returns true if user is authenticated with a UserToken.
 */
function canTrack(): boolean {
  const authStore = useAuthStore()
  return authStore.selectedToken != null && isUserToken(authStore.selectedToken.payload)
}

/**
 * Low-level function to send activity to the API.
 * This is a fire-and-forget operation - errors are logged but not thrown.
 */
function sendActivity(activity: UserActivity): void {
  if (!canTrack()) return

  apiFetch
    .POST('/application--user-activities', {
      // @ts-expect-error The API schema doesn't have a proper request body type for this endpoint
      body: activity,
    })
    .catch((error: unknown) => {
      // Fire and forget - just log errors
      console.error('Failed to track user activity:', error)
    })
}

/**
 * Track portal-related activities like login, logout, token refresh, or user switch.
 */
export function trackPortalActivity(name: string, action: PortalAction): void {
  sendActivity({
    name,
    timestamp: new Date(),
    portal: action,
  })
}

/**
 * Track route navigation.
 * @param routeName - The name of the route being navigated to
 * @param resource - Optional resource name (used as activity name if provided, otherwise 'navigation')
 */
export function trackRouteChange(routeName: string, resource?: string): void {
  sendActivity({
    name: resource ?? 'navigation',
    timestamp: new Date(),
    route: routeName,
  })
}

/**
 * Track domain-specific activities like opening, editing, or adding to favorites.
 */
export function trackDomainActivity(resource: Resource, action?: ResourceAction): void {
  sendActivity({
    name: 'item',
    timestamp: new Date(),
    domain: resource,
    ...(action != null ? { domain_action: action } : {}),
  })
}

/**
 * Track language/locale changes.
 */
export function trackLanguageSwitch(from: string, to: string): void {
  sendActivity({
    name: 'user_settings',
    timestamp: new Date(),
    language_switch: { from, to },
  })
}
